import { ChartData, ChartDataset } from 'chart.js'
import { linearFit } from '@lla-platform/core/core-util'

/** '%': values are shares on a 0-100 axis. '': plain counts (units). */
export type TrendUnit = '%' | ''

/** One line of a trend chart; its color is read from the CSS variable, so light and dark themes follow it. */
export interface ITrendSeries<K extends string = string> {
  key: K
  label: string
  colorVar: string
}

/** A series' value at one bucket; null leaves a gap in the line. */
export type TrendValueFn<P, K extends string = string> = (point: P, key: K) => number | null
/** A series' tooltip text at one bucket; empty hides it. */
export type TrendFormatFn<P, K extends string = string> = (point: P, key: K) => string

// Trendline color by direction; `flat` for a zero slope
export interface ITrendlineColors {
  up: string
  down: string
  flat: string
}

export type ITrendChartDataset = ChartDataset<'line', (number | null)[]> & {
  formatted: string[]
  isTrendline?: boolean
}

export const FULL_SCALE_Y_MAX = 100

export const visibleTrendSeries = <K extends string>(
  series: ITrendSeries<K>[],
  hidden: K[]
): ITrendSeries<K>[] => series.filter(({ key }) => !hidden.includes(key))

// Overall movement of the single visible series' trendline across the range, as a percent.
// '%' unit: fitted value at the last bucket minus the first, in percentage points.
// '' unit: that movement relative to the fitted first value; when the line starts at or below 0 it is
// measured against the series mean instead. Undefined unless exactly one series is visible and there
// is a base to measure from.
export const trendChange = <P, K extends string>(
  points: P[],
  series: ITrendSeries<K>[],
  value: TrendValueFn<P, K>,
  hidden: K[],
  unit: TrendUnit
): number | undefined => {
  const visible = visibleTrendSeries(series, hidden)
  if (visible.length !== 1) {
    return undefined
  }
  const values = points.map((point) => value(point, visible[0].key))
  const fit = linearFit(values)
  if (!fit) {
    return undefined
  }
  const change = fit.slope * (points.length - 1)
  if (unit === '%') {
    return change
  }
  const defined = values.filter((el): el is number => el !== null)
  const mean = defined.reduce((sum, el) => sum + el, 0) / defined.length
  const base = fit.intercept > 0 ? fit.intercept : mean
  return base > 0 ? (change / base) * 100 : undefined
}

export const buildTrendChartData = <P, K extends string>(
  labels: string[],
  points: P[],
  series: ITrendSeries<K>[],
  value: TrendValueFn<P, K>,
  format: TrendFormatFn<P, K>,
  colors: Record<K, string>,
  trendlineColors: ITrendlineColors,
  hidden: K[] = []
): ChartData<'line', (number | null)[]> => ({
  labels,
  datasets: [
    ...series.map(({ key, label }): ITrendChartDataset => ({
      type: 'line',
      label,
      hidden: hidden.includes(key),
      data: points.map((point) => value(point, key)),
      formatted: points.map((point) => format(point, key)),
      borderColor: colors[key],
      backgroundColor: 'transparent',
      pointBackgroundColor: colors[key],
      borderWidth: 2,
      pointRadius: 1.5,
      pointHoverRadius: 4,
      tension: 0.3,
      spanGaps: false
    })),
    ...trendlineDataset(points, series, value, trendlineColors, hidden)
  ]
})

// Thin least-squares line for the single visible series, colored by direction; nothing when 0 or 2+ are shown
const trendlineDataset = <P, K extends string>(
  points: P[],
  series: ITrendSeries<K>[],
  value: TrendValueFn<P, K>,
  colors: ITrendlineColors,
  hidden: K[]
): ITrendChartDataset[] => {
  const visible = visibleTrendSeries(series, hidden)
  if (visible.length !== 1) {
    return []
  }
  const { key, label } = visible[0]
  const fit = linearFit(points.map((point) => value(point, key)))
  if (!fit) {
    return []
  }
  const color = fit.slope > 0 ? colors.up : fit.slope < 0 ? colors.down : colors.flat
  return [
    {
      type: 'line',
      label: `${label} trend`,
      isTrendline: true,
      data: points.map((_, i) => fit.slope * i + fit.intercept),
      formatted: [],
      borderColor: color,
      backgroundColor: 'transparent',
      borderWidth: 1.5,
      pointRadius: 0,
      tension: 0
    }
  ]
}
